import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const socket = io('http://localhost:3001');

// ─── THEME ───────────────────────────────────────────────────────────────────
const C = {
  bg: '#070b14', sidebar: '#0a0f1e', panel: '#0e1525',
  panelHover: '#131c30', border: '#1a2a45', borderLight: '#243558',
  gold: '#f5c842', blue: '#4a9eff', orange: '#ff8c42',
  red: '#ff4455', green: '#00e676', purple: '#9c6fff',
  text: '#ddeeff', muted: '#556688', dimmed: '#334466',
  star: '#ffffff',
};

// ─── STARFIELD ────────────────────────────────────────────────────────────────
function Starfield() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const stars = Array.from({ length: 180 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.2 + 0.2,
      speed: Math.random() * 0.15 + 0.02,
      opacity: Math.random() * 0.7 + 0.1,
      twinkle: Math.random() * Math.PI * 2,
    }));
    let frame;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.twinkle += 0.015;
        const op = s.opacity * (0.6 + 0.4 * Math.sin(s.twinkle));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,220,255,${op})`;
        ctx.fill();
      });
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />;
}

// ─── NAV CONFIG ───────────────────────────────────────────────────────────────
const NAV = [
  { id: 'tasks',     label: 'Tasks',     icon: '⊞' },
  { id: 'pipeline',  label: 'Pipeline',  icon: '▸' },
  { id: 'approvals', label: 'Approvals', icon: '✓', badge: 1 },

  { id: 'calendar',  label: 'Calendar',  icon: '▦' },
  { id: 'projects',  label: 'Projects',  icon: '▢' },
  { id: 'memory',    label: 'Memory',    icon: '◉' },
  { id: 'docs',      label: 'Docs',      icon: '▤' },
  { id: 'people',    label: 'People',    icon: '◎' },
  { id: 'office',    label: 'Office',    icon: '⌂' },
  { id: 'residuals', label: 'Residuals', icon: '💰' },
  { id: 'merchants', label: 'Merchants', icon: '🏪' },
  { id: 'reports',   label: 'Reports',   icon: '📊' },
  { id: 'team',      label: 'Team',      icon: '◈' },
];

// ─── AGENT DEFINITIONS ────────────────────────────────────────────────────────
const AGENTS = [
  { id: 'agent85',   name: 'Agent 85',  role: 'Chief of Staff',   color: C.gold,   emoji: '🕵️', tags: ['Command','Strategy','Loyalty'],    desc: 'Orchestrates, delegates, keeps the mission on track. First point of contact between Chris and the crew.' },
  { id: 'peter',     name: 'Peter',     role: 'Lead Hunter',      color: C.blue,   emoji: '🔍', tags: ['Apollo','Speed','Radar'],           desc: 'Finds, tracks, and qualifies leads. Feeds the top of the funnel 24/7.' },
  { id: 'samir',     name: 'Samir',     role: 'Lead Enricher',    color: C.orange, emoji: '⚗️', tags: ['Data','Precision','Quality'],       desc: 'Enriches every lead with business intel before it hits GHL.' },
  { id: 'bolton',    name: 'Bolton',    role: 'Email Writer',      color: '#7ec8ff',emoji: '✉️', tags: ['Voice','Persuasion','Sequences'],   desc: 'Writes email sequences, handles copy, and drives prospects to book.' },
  { id: 'milton',    name: 'Milton',    role: 'Email Monitor',     color: C.red,    emoji: '👁️', tags: ['Attention','Alerts','Vigilance'],   desc: 'Watches the inbox, flags replies, escalates hot leads.' },
  { id: 'lumbergh',  name: 'Lumbergh',  role: 'GHL Manager',      color: '#c8a0ff',emoji: '📋', tags: ['CRM','Booking','Follow-up'],        desc: "Manages Go High Level. Books demos. Makes sure no lead falls through the cracks. Yeah, that'd be great." },
];

// ─── TICKER BANNER ──────────────────────────────────────────────────────────
function TickerBanner() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => fetch('/api/ticker').then(r => r.json()).then(setData).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const fmt = n => '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const items = data ? [
    { label: 'RESIDUAL', value: fmt(data.residualTotal), color: '#ffd700', sub: data.residualMonth },
    { label: 'VOLUME', value: fmt(data.totalVolume), color: '#ffd700' },
    { label: 'MIDs', value: String(data.totalMids), color: '#00ff88' },
    { label: 'AVG REP PAY', value: fmt(data.avgRepPay), color: '#ffd700' },
    { label: 'TOP MERCHANT', value: `${data.topMerchantName}  ${fmt(data.topMerchantRepPay)}`, color: '#00ff88' },
  ] : [{ label: 'LOADING', value: '...', color: '#556688' }];

  const content = items.map(i => `${i.label}: ${i.sub ? i.sub + ' ' : ''}${i.value}`).join('    ◆    ');
  // Duplicate for seamless loop
  const doubled = content + '    ◆    ' + content;

  return (
    <div style={{
      height: 40, background: '#0a0a1a', borderBottom: '1px solid #1a2a45',
      overflow: 'hidden', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center',
    }}>
      <style>{`
        @keyframes tickerScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
      `}</style>
      <div style={{
        display: 'inline-block', whiteSpace: 'nowrap', fontFamily: "'Courier New', Courier, monospace",
        fontSize: 13, fontWeight: 700, letterSpacing: 0.5,
        animation: 'tickerScroll 30s linear infinite',
      }}>
        {data ? (
          <>
            {[0, 1].map(copy => (
              <React.Fragment key={copy}>
                {items.map((item, i) => (
                  <React.Fragment key={`${copy}-${i}`}>
                    <span style={{ color: '#888', marginRight: 4 }}>{item.label}:</span>
                    {item.sub && <span style={{ color: '#aaa', marginRight: 4 }}>{item.sub}</span>}
                    <span style={{ color: item.color }}>{item.value}</span>
                    <span style={{ color: '#333', margin: '0 16px' }}>◆</span>
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </>
        ) : (
          <span style={{ color: '#556688' }}>Loading ticker data...</span>
        )}
      </div>
    </div>
  );
}

// ─── STATUS CHIP ─────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const map = { working: C.green, writing: C.blue, enriching: C.orange,
                monitoring: C.blue, managing: C.purple, commanding: C.gold,
                chatting: C.purple, walking: C.orange, error: C.red, idle: C.dimmed };
  const col = map[status] || C.dimmed;
  return (
    <span style={{ background: col+'1a', color: col, border: `1px solid ${col}44`,
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
      {status === 'error' ? '⚠ ' : status === 'working' || status === 'writing' ? '● ' : '○ '}{status}
    </span>
  );
}

// ─── MISSION BAR ─────────────────────────────────────────────────────────────
function MissionBar({ mission, onPing }) {
  const pct = mission ? Math.round((mission.current / mission.goal) * 100) : 45;
  const fmt = n => '$' + (n||0).toLocaleString();
  return (
    <div style={{ height: 44, background: C.sidebar+'ee', borderBottom: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', padding: '0 16px', gap: 20, zIndex: 10, position: 'relative', backdropFilter: 'blur(8px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14 }}>⊞</span>
        <span style={{ color: C.text, fontSize: 13, fontWeight: 600, letterSpacing: 0.5 }}>Mission Control</span>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 200, height: 4, background: C.border, borderRadius: 2 }}>
          <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg,${C.blue},${C.gold})`, borderRadius: 2, transition: 'width 1s ease' }} />
        </div>
        <span style={{ color: C.green, fontWeight: 700, fontSize: 13 }}>{fmt(mission?.current||38650)}</span>
        <span style={{ color: C.muted, fontSize: 11 }}>/ {fmt(mission?.goal||85000)}</span>
        <span style={{ color: C.gold, fontWeight: 700 }}>{pct}%</span>
        <span style={{ color: C.orange, fontSize: 11 }}>🎂 {mission?.daysLeft||456}d</span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onPing} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.text, padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
          Ping Agent 85
        </button>
        <button style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>⟳</button>
      </div>
    </div>
  );
}

// ─── PIXEL OFFICE ─────────────────────────────────────────────────────────────
function Office({ agentData, waterCooler }) {
  const [agentPositions, setAgentPositions] = useState({
    agent85:  { x: 380, y: 60,  status: 'commanding', dir: 1 },
    peter:    { x: 80,  y: 120, status: 'working',    dir: 1 },
    samir:    { x: 180, y: 220, status: 'enriching',  dir: -1 },
    bolton:   { x: 280, y: 120, status: 'writing',    dir: 1 },
    milton:   { x: 80,  y: 320, status: 'monitoring', dir: 1 },
    lumbergh: { x: 380, y: 220, status: 'managing',   dir: -1 },
  });
  const [activeMode, setActiveMode] = useState('working');
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setFrame(f => f + 1), 600);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (activeMode !== 'gather' && activeMode !== 'meeting') {
      const t = setInterval(() => {
        setAgentPositions(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(id => {
            if (Math.random() < 0.25) {
              const a = next[id];
              const dx = (Math.random() - 0.5) * 30;
              const dy = (Math.random() - 0.5) * 20;
              next[id] = { ...a, x: Math.max(20, Math.min(460, a.x + dx)), y: Math.max(20, Math.min(360, a.y + dy)), dir: dx > 0 ? 1 : -1 };
            }
          });
          return next;
        });
      }, 2000);
      return () => clearInterval(t);
    }
    if (activeMode === 'gather') {
      setAgentPositions(prev => {
        const next = { ...prev };
        const cx = 250, cy = 230;
        const angle = (2 * Math.PI) / 6;
        Object.keys(next).forEach((id, i) => {
          next[id] = { ...next[id], x: cx + 70 * Math.cos(angle * i), y: cy + 50 * Math.sin(angle * i), status: 'chatting' };
        });
        return next;
      });
    }
    if (activeMode === 'meeting') {
      setAgentPositions(prev => {
        const next = { ...prev };
        const seats = [{x:230,y:195},{x:270,y:195},{x:290,y:230},{x:270,y:265},{x:230,y:265},{x:210,y:230}];
        Object.keys(next).forEach((id, i) => {
          next[id] = { ...next[id], x: seats[i]?.x||250, y: seats[i]?.y||230, status: 'chatting' };
        });
        return next;
      });
    }
  }, [activeMode]);

  function AgentSprite({ id, pos }) {
    const def = AGENTS.find(a => a.id === id);
    const isErr = (agentData?.[id]?.status || pos.status) === 'error';
    const bobY = Math.sin(frame * 0.8 + (id.charCodeAt(0) || 0)) * 2;
    const statusColors = { working: C.green, writing: C.blue, enriching: C.orange,
      monitoring: C.blue, managing: C.purple, commanding: C.gold, chatting: C.purple, error: C.red };
    const col = isErr ? C.red : (statusColors[pos.status] || C.blue);
    const walk = frame % 2 === 0;

    return (
      <div style={{ position: 'absolute', left: pos.x, top: pos.y + bobY,
        transform: pos.dir < 0 ? 'scaleX(-1)' : 'scaleX(1)',
        transition: 'left 1.5s ease, top 1.5s ease', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 5 }}>
        {/* Name */}
        <div style={{ fontSize: 9, color: C.text, fontWeight: 700, marginBottom: 1,
          transform: pos.dir < 0 ? 'scaleX(-1)' : 'none', whiteSpace: 'nowrap',
          textShadow: '0 1px 4px #000', letterSpacing: 0.5 }}>{def?.name}</div>
        {/* Head */}
        <div style={{ width: 14, height: 14, borderRadius: '50%', background: def?.color || C.blue,
          border: `2px solid ${col}`, boxShadow: `0 0 8px ${col}66`, marginBottom: 1 }}>
          <div style={{ fontSize: 8, textAlign: 'center', lineHeight: '12px' }}>{def?.emoji}</div>
        </div>
        {/* Body */}
        <div style={{ width: 10, height: 10, background: col+'cc', borderRadius: 2 }} />
        {/* Legs */}
        <div style={{ display: 'flex', gap: 2 }}>
          <div style={{ width: 3, height: walk ? 8 : 5, background: col+'aa', borderRadius: 1,
            transition: 'height 0.3s', marginTop: walk ? 0 : 3 }} />
          <div style={{ width: 3, height: walk ? 5 : 8, background: col+'aa', borderRadius: 1,
            transition: 'height 0.3s', marginTop: walk ? 3 : 0 }} />
        </div>
        {/* Status dot */}
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: isErr ? C.red : col,
          marginTop: 2, boxShadow: `0 0 4px ${col}` }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 14, height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginRight: 8 }}>
            {[['working','● Working',C.green],['chatting','● Chatting',C.purple],['walking','● Walking',C.orange],['idle','○ Idle',C.dimmed]].map(([k,l,col])=>(
              <span key={k} style={{ fontSize: 10, color: col, marginRight: 8 }}>{l}</span>
            ))}
          </div>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 10, color: C.muted }}>Demo Controls</span>
          {[['working','All Working'],['gather','Gather'],['meeting','Run Meeting'],['watercooler','Watercooler']].map(([mode,label])=>(
            <button key={mode} onClick={() => setActiveMode(mode)} style={{
              background: activeMode===mode ? C.border : 'transparent',
              border: `1px solid ${C.border}`, color: activeMode===mode ? C.text : C.muted,
              padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11,
            }}>{label}</button>
          ))}
        </div>

        {/* Floor plan */}
        <div style={{ flex: 1, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, position: 'relative', overflow: 'hidden' }}>
          {/* Starfield-tinted bg */}
          <div style={{ position: 'absolute', inset: 0,
            backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 28px,#111d3322 28px,#111d3322 29px),repeating-linear-gradient(90deg,transparent,transparent 28px,#111d3322 28px,#111d3322 29px)',
            backgroundSize: '29px 29px' }} />

          {/* Desks */}
          {[
            { x: 60,  y: 100, label: 'Peter', col: C.blue },
            { x: 60,  y: 290, label: 'Milton', col: C.red },
            { x: 160, y: 200, label: 'Samir', col: C.orange },
            { x: 260, y: 100, label: 'Bolton', col: '#7ec8ff' },
            { x: 360, y: 200, label: 'Lumbergh', col: C.purple },
            { x: 360, y: 40,  label: 'Agent 85', col: C.gold },
          ].map(d => (
            <div key={d.label} style={{ position: 'absolute', left: d.x, top: d.y }}>
              <div style={{ width: 44, height: 30, background: '#0d1a2d', border: `1px solid ${d.col}44`,
                borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 28, height: 18, background: d.col+'11', border: `1px solid ${d.col}66`, borderRadius: 2,
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, padding: 2 }}>
                  {[70,90,60].map((w,i)=><div key={i} style={{ height: 2, width: `${w}%`, background: d.col+'88', borderRadius: 1 }}/>)}
                </div>
              </div>
            </div>
          ))}

          {/* Conference table */}
          <div style={{ position: 'absolute', left: 200, top: 195, width: 90, height: 60, background: '#111d30',
            border: `1px solid ${C.border}`, borderRadius: '50%', opacity: 0.7 }} />

          {/* Water cooler */}
          <div style={{ position: 'absolute', left: 20, top: 20 }}>
            <div style={{ width: 14, height: 24, background: '#1a5a8a', border: `1px solid ${C.blue}66`, borderRadius: '4px 4px 2px 2px', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 2 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.blue+'44', border: `1px solid ${C.blue}` }} />
            </div>
            <div style={{ width: 18, height: 5, background: '#0d1a2d', borderRadius: 1, marginTop: 1 }} />
          </div>

          {/* Plants */}
          {[[450,30],[450,350],[20,350]].map(([x,y],i)=>(
            <div key={i} style={{ position: 'absolute', left: x, top: y, fontSize: 20 }}>🌿</div>
          ))}

          {/* Agent sprites */}
          {Object.entries(agentPositions).map(([id, pos]) => (
            <AgentSprite key={id} id={id} pos={pos} />
          ))}
        </div>
      </div>

      {/* Office Space Quotes */}
      <div style={{ width: 240, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ color: C.muted, fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>📋 OFFICE MEMO</div>
          <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { agent: 'Lumberg', msg: "Yeaaaah... I'm going to need you to go ahead and close more deals. That'd be greeeeat." },
              { agent: 'Milton', msg: 'I was told there would be residuals. And I could process my merchants by the window.' },
              { agent: 'Bolton', msg: 'PC Load Letter?! What the $#@% does that mean?!' },
              { agent: 'Samir', msg: 'It\'s not that I\'m lazy, it\'s that I just don\'t care about your flat-rate pricing.' },
              { agent: 'Peter', msg: 'What would you say... ya do here? I found 3 new leads. Just saying.' },
            ].map((q,i) => (
              <div key={i} style={{ padding: '6px 8px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 10 }}>
                <div style={{ color: C.gold, fontSize: 9, marginBottom: 2 }}>{q.agent}</div>
                <div style={{ color: C.muted }}>{q.msg}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TEAM VIEW ────────────────────────────────────────────────────────────────
function Team() {
  return (
    <div style={{ overflow: 'auto', height: '100%' }}>
      <div style={{ textAlign: 'center', padding: '20px 0 28px' }}>
        <div style={{ color: C.muted, fontSize: 12, letterSpacing: 3, marginBottom: 12, textTransform: 'uppercase' }}>
          Agent 85 Mission Control — Office Space Division
        </div>
        <div style={{ color: C.text, fontSize: 28, fontWeight: 700, letterSpacing: -0.5, marginBottom: 10 }}>Meet the Crew</div>
        <div style={{
          display: 'inline-block',
          background: `linear-gradient(135deg, ${C.gold}22, ${C.blue}22)`,
          border: `1px solid ${C.gold}44`,
          borderRadius: 10, padding: '12px 28px', marginBottom: 8,
        }}>
          <div style={{ color: C.gold, fontSize: 15, fontWeight: 700, letterSpacing: 0.5 }}>
            "We are Continuous Autonomous Velocity AI moving towards our goal"
          </div>
        </div>
        <div style={{ color: C.dimmed, fontSize: 12, marginTop: 8, maxWidth: 520, margin: '10px auto 0' }}>
          6 agents running 24/7 on a Mac Mini in Reno. Finding leads. Writing emails. Booking demos. Closing deals. All without being told what to do every single day.
        </div>
      </div>

      {/* Chief card */}
      <div style={{ maxWidth: 500, margin: '0 auto 20px', background: C.panel, border: `1px solid ${C.gold}44`, borderRadius: 12, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, boxShadow: `0 0 20px ${C.gold}44` }}>🕵️</div>
          <div>
            <div style={{ color: C.gold, fontWeight: 700, fontSize: 18 }}>Agent 85</div>
            <div style={{ color: C.muted, fontSize: 12 }}>Chief of Staff — Coordinates, delegates, keeps the mission alive. First point of contact between Chris and the crew.</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          {['Command','Strategy','Memory','Loyalty','Vigilance'].map(t=>(
            <span key={t} style={{ background: C.gold+'22', color: C.gold, border: `1px solid ${C.gold}33`, padding: '2px 8px', borderRadius: 4, fontSize: 10 }}>{t}</span>
          ))}
        </div>
      </div>

      {/* Pipeline visualization */}
      <div style={{ textAlign: 'center', color: C.muted, fontSize: 10, letterSpacing: 3, marginBottom: 12 }}>↓ LEAD PIPELINE ↓</div>

      {/* Crew grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, maxWidth: 700, margin: '0 auto', paddingBottom: 20 }}>
        {AGENTS.filter(a => a.id !== 'agent85').map(a => (
          <div key={a.id} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, boxShadow: `0 0 12px ${a.color}44` }}>{a.emoji}</div>
              <div>
                <div style={{ color: C.text, fontWeight: 700 }}>{a.name}</div>
                <div style={{ color: C.muted, fontSize: 11 }}>{a.role}</div>
              </div>
            </div>
            <div style={{ color: C.dimmed, fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>{a.desc}</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {a.tags.map(t=>(
                <span key={t} style={{ background: a.color+'1a', color: a.color, border: `1px solid ${a.color}33`, padding: '2px 7px', borderRadius: 4, fontSize: 10 }}>{t}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center', color: C.muted, fontSize: 10, letterSpacing: 3, paddingBottom: 20 }}>● META LAYER ●</div>
    </div>
  );
}

// ─── PIPELINE (Lead Kanban) ───────────────────────────────────────────────────
const PIPELINE_COLS = ['Leads','Enriched','Emailed','Replied','Demo Booked','Closed'];
const COL_COLORS = [C.blue, C.orange, C.purple, C.gold, C.green, C.green];

function Pipeline() {
  const [cards, setCards] = useState({
    'Leads':       [{ id:1, title:'Acme Payments LLC', sub:'$2.3M/yr processing', tag:'apollo' },{ id:2,title:'FastPay Solutions',sub:'$890K/yr', tag:'apollo' }],
    'Enriched':    [{ id:3, title:'Mountain West Retail', sub:'LinkedIn: 120 employees', tag:'samir' }],
    'Emailed':     [{ id:4, title:'Nevada Auto Group', sub:'Seq 2 sent · 48% open', tag:'bolton' },{ id:5,title:'Reno Restaurant Co',sub:'Seq 1 sent',tag:'bolton' }],
    'Replied':     [{ id:6, title:'Sierra Fuel Co', sub:'Hot lead · replied today', tag:'milton' }],
    'Demo Booked': [{ id:7, title:'Basin Electric', sub:'Demo: Mon 10am', tag:'lumbergh' }],
    'Closed':      [{ id:8, title:'Cascade Grocers', sub:'$1,200/mo residual', tag:'lumbergh' }],
  });

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Lead Pipeline</div>
        <span style={{ color: C.muted, fontSize: 12, marginLeft: 10 }}>Leads → Enriched → Emailed → Replied → Demo → Closed</span>
        <button style={{ marginLeft: 'auto', background: C.gold, color: '#000', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>+ New Lead</button>
      </div>
      <div style={{ display: 'flex', gap: 10, minWidth: 900 }}>
        {PIPELINE_COLS.map((col, ci) => (
          <div key={col} style={{ flex: 1, minWidth: 140 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: COL_COLORS[ci] }} />
              <span style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>{col.toUpperCase()}</span>
              <span style={{ color: C.dimmed, fontSize: 11 }}>{(cards[col]||[]).length}</span>
              <span style={{ marginLeft: 'auto', color: C.dimmed, cursor: 'pointer', fontSize: 14 }}>+</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minHeight: 60 }}>
              {(cards[col]||[]).map(card => (
                <div key={card.id} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, cursor: 'grab' }}>
                  <div style={{ color: C.text, fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{card.title}</div>
                  <div style={{ color: C.muted, fontSize: 10, marginBottom: 6 }}>{card.sub}</div>
                  <span style={{ background: COL_COLORS[ci]+'22', color: COL_COLORS[ci], border: `1px solid ${COL_COLORS[ci]}33`, padding: '1px 6px', borderRadius: 3, fontSize: 9 }}>{card.tag}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CALENDAR VIEW ────────────────────────────────────────────────────────────
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const CRON_JOBS = [
  { name: 'morning brief', time: '6:00 AM', color: C.blue, days: 'daily' },
  { name: 'lead pipeline check', time: '9:00 AM', color: C.green, days: 'daily' },
  { name: 'email sequence fire', time: '10:00 AM', color: C.orange, days: 'daily' },
  { name: 'demo follow-up', time: '2:00 PM', color: C.purple, days: 'weekdays' },
  { name: 'mission report', time: '6:00 PM', color: C.gold, days: 'daily' },
];

function Calendar() {
  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Scheduled Tasks</div>
        <span style={{ color: C.muted, fontSize: 12, marginLeft: 10 }}>Agent 85's automated routines</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button style={{ background: C.border, color: C.text, border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}>Week</button>
          <button style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}>Today</button>
        </div>
      </div>

      {/* Always running */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 14 }}>
        <div style={{ color: C.green, fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>● ALWAYS RUNNING</div>
        <span style={{ background: C.blue+'22', color: C.blue, border: `1px solid ${C.blue}33`, padding: '3px 10px', borderRadius: 4, fontSize: 11 }}>mission control check • Every 30 min</span>
      </div>

      {/* Weekly grid */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: `1px solid ${C.border}` }}>
          {DAYS.map(d => (
            <div key={d} style={{ padding: '8px 10px', color: C.muted, fontSize: 11, fontWeight: 700, borderRight: `1px solid ${C.border}` }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', minHeight: 180 }}>
          {DAYS.map((d, di) => (
            <div key={d} style={{ padding: 6, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {CRON_JOBS.filter(j => j.days === 'daily' || (j.days === 'weekdays' && di >= 1 && di <= 5)).map(job => (
                <div key={job.name} style={{ background: job.color+'22', border: `1px solid ${job.color}44`, borderRadius: 4, padding: '3px 5px' }}>
                  <div style={{ color: job.color, fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.name}</div>
                  <div style={{ color: C.muted, fontSize: 9 }}>{job.time}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Next up */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
        <div style={{ color: C.muted, fontSize: 10, letterSpacing: 2, marginBottom: 10 }}>NEXT UP</div>
        {CRON_JOBS.map(job => (
          <div key={job.name} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: job.color, marginRight: 10 }} />
            <span style={{ color: C.text, fontSize: 12, flex: 1 }}>{job.name}</span>
            <span style={{ color: C.muted, fontSize: 11 }}>{job.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── APPROVALS VIEW ──────────────────────────────────────────────────────────
function Approvals() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/briefs').then(r => r.json()).then(d => { setBriefs(d||[]); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const approve = (id, decision, note = '') => {
    fetch(`/api/briefs/${id}`, { method: 'PATCH', headers: {'Content-Type':'application/json'}, body: JSON.stringify({ status: decision, note, decidedAt: new Date().toISOString() }) })
      .then(r => r.json())
      .then(updated => setBriefs(prev => prev.map(b => b.id === id ? updated : b)));
  };

  const fmt = n => '$' + (n||0).toLocaleString();

  return (
    <div style={{ display: 'flex', gap: 14, height: '100%', overflow: 'hidden' }}>
      {/* Main approval feed */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {loading && <div style={{ color: C.muted, padding: 40, textAlign: 'center' }}>Loading briefs...</div>}
        {!loading && briefs.filter(b => b.status === 'pending').map(brief => (
          <div key={brief.id} style={{ background: C.panel, border: `1px solid ${C.gold}44`, borderRadius: 12, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '14px 20px', background: C.gold+'11', borderBottom: `1px solid ${C.gold}33`, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 24 }}>🕵️</div>
              <div>
                <div style={{ color: C.gold, fontWeight: 700, fontSize: 15 }}>Agent 85 — Morning Brief</div>
                <div style={{ color: C.muted, fontSize: 12 }}>{brief.createdAt ? new Date(brief.createdAt).toLocaleString() : brief.date || 'Today'}</div>
              </div>
              <div style={{ marginLeft: 'auto', background: C.gold+'22', color: C.gold, border: `1px solid ${C.gold}44`, padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>PENDING APPROVAL</div>
            </div>

            {/* Brief content */}
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Summary text */}
              {brief.summary && (
                <div style={{ color: C.text, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{brief.summary}</div>
              )}
              {/* Items list */}
              {(brief.items||[]).map((item, i) => (
                <div key={i} style={{ padding: '8px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, lineHeight: 1.5 }}>
                  {item}
                </div>
              ))}

              {/* Approve / Reject */}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button onClick={() => approve(brief.id, 'approved')} style={{
                  flex: 1, background: `linear-gradient(135deg, ${C.green}cc, ${C.blue}cc)`, color: '#000',
                  border: 'none', borderRadius: 8, padding: '14px 0', fontWeight: 700, fontSize: 16, cursor: 'pointer',
                }}>✅ APPROVE</button>
                <button onClick={() => approve(brief.id, 'rejected', 'Needs revision')} style={{
                  flex: 1, background: C.panel, color: C.muted,
                  border: `1px solid ${C.border}`, borderRadius: 8, padding: '14px 0', fontWeight: 700, fontSize: 16, cursor: 'pointer',
                }}>✗ REVISE</button>
              </div>
            </div>
          </div>
        ))}

        {!loading && briefs.filter(b => b.status === 'pending').length === 0 && (
          <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>All caught up</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 6 }}>Next morning brief arrives at 6AM</div>
          </div>
        )}
      </div>

            {/* History sidebar */}
      <div style={{ width: 220, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, flex: 1 }}>
          <div style={{ color: C.muted, fontSize: 10, letterSpacing: 2, marginBottom: 10 }}>HISTORY</div>
          {briefs.filter(b => b.status !== 'pending').map((h, i) => (
            <div key={i} style={{ padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 12 }}>{h.status === 'approved' ? '✅' : '✗'}</span>
                <span style={{ color: h.status === 'approved' ? C.green : C.red, fontSize: 11, fontWeight: 700 }}>{h.status.toUpperCase()}</span>
              </div>
              <div style={{ color: C.muted, fontSize: 10 }}>{h.createdAt ? new Date(h.createdAt).toLocaleDateString() : h.date}</div>
              {h.note && <div style={{ color: C.dimmed, fontSize: 10, marginTop: 2, fontStyle: 'italic' }}>"{h.note}"</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── DOCS VIEW ────────────────────────────────────────────────────────────────
function Docs() {
  const [selected, setSelected] = useState('playbook');
  const docs = {
    playbook: {
      title: '📋 Merchant Processing Playbook',
      content: `# Merchant Processing Playbook

## The Pitch
We save businesses money on credit card processing. Period. No fluff.

**Opening:** "We work with businesses like yours to audit your current processing rates. Most businesses we talk to are overpaying by 15-30%. We just need 10 minutes to show you what you're leaving on the table."

## Qualifying Questions
1. What processor are you currently with?
2. What's your average monthly volume?
3. What's your average ticket size?
4. Do you accept mostly card-present or card-not-present?

## Objection Handling
- **"We're happy with our current processor"** → "That's great — but happy is different from optimal. Can I show you a comparison?"
- **"Rates look fine to us"** → "Most business owners say that until we run the numbers. Our audit is free — you have nothing to lose."
- **"We're under contract"** → "We can still run the analysis so you're ready when it expires."

## Residual Model
- Average client: $1,200-2,000/month residual
- Close rate target: 15% of demos
- Demo target: 12/month → 2 closes → $2,400/month new MRR
- Goal: $85K total MRR by Chris's 50th birthday`,
    },
    icp: {
      title: '🎯 Ideal Customer Profile',
      content: `# Ideal Customer Profile (ICP)

## Best Fit Industries
- Auto dealerships (high volume, predictable)
- Restaurants & food service (high frequency)
- Medical/dental offices (high ticket)
- Retail chains (volume + consistency)
- Gas stations & fuel companies (massive volume)

## Qualifying Criteria
- Monthly processing volume: $50K+
- In business 2+ years
- Currently processing with a big bank or incumbent processor
- Business owner / decision maker reachable via email

## Apollo Filters (Peter's Setup)
- Title: Owner, President, CFO, Controller
- Employee count: 5-250
- Industry: [see above]
- Geography: Western US (Reno, Reno metro, NV, CA, UT, ID, OR, WA)
- Revenue: $500K+/year estimated

## Red Flags
- Under contract with early termination > $500
- Very low volume (under $20K/month)
- Brand new business (under 6 months)`,
    },
    onboarding: {
      title: '🚀 New Client Onboarding',
      content: `# New Client Onboarding Checklist

## Day 1 — Contract Signed
- [ ] Send welcome email (Bolton)
- [ ] Collect MPA (Merchant Processing Application)
- [ ] Gather 3 months of processing statements
- [ ] Submit to processor
- [ ] Update GHL status to "Submitted" (Lumbergh)

## Week 1 — Approval
- [ ] Follow up with processor daily
- [ ] Update client on status
- [ ] Prep equipment if needed (terminal, reader)
- [ ] Schedule go-live date

## Go-Live Day
- [ ] Test transaction
- [ ] Confirm funding schedule
- [ ] Set up autopay for monthly fee
- [ ] Update GHL to "Active" (Lumbergh)
- [ ] Log first residual estimate in dashboard

## Ongoing
- [ ] Monthly statement review
- [ ] Annual rate review
- [ ] Referral request at 90 days`,
    },
    ghl: {
      title: '⚙️ GHL Pipeline Setup',
      content: `# Go High Level Pipeline Configuration

## Pipeline Stages
1. **New Lead** — Peter drops leads here from Apollo
2. **Enriched** — Samir moves here after enrichment
3. **Contacted** — Bolton moves here after first email
4. **Replied** — Milton moves here when reply detected
5. **Demo Booked** — Lumbergh moves here when calendly confirmed
6. **Demo Completed** — After the call
7. **Proposal Sent** — After demo
8. **Closed Won** — Deal done 🎉
9. **Closed Lost** — Not a fit, not now

## Automation Rules (Lumbergh manages)
- New Lead → auto-enroll in Bolton Sequence 1
- Reply detected → notify Milton → escalate to Chris
- Demo booked → send confirmation email → add to calendar
- No reply after Seq 3 → move to 90-day nurture

## Custom Fields
- Processing Volume (monthly)
- Current Processor
- Contract Expiry Date
- Estimated Residual
- Source (Apollo / Referral / Inbound)`,
    },
  };

  const docList = Object.entries(docs).map(([id, d]) => ({ id, title: d.title }));

  return (
    <div style={{ display: 'flex', gap: 14, height: '100%', overflow: 'hidden' }}>
      {/* Doc list */}
      <div style={{ width: 220, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ color: C.muted, fontSize: 10, letterSpacing: 2, marginBottom: 6 }}>DOCUMENTS</div>
        {docList.map(d => (
          <button key={d.id} onClick={() => setSelected(d.id)} style={{
            background: selected === d.id ? C.border : 'transparent',
            border: selected === d.id ? `1px solid ${C.borderLight}` : `1px solid transparent`,
            borderRadius: 7, padding: '9px 12px', color: selected === d.id ? C.text : C.muted,
            cursor: 'pointer', textAlign: 'left', fontSize: 12, fontFamily: 'inherit',
          }}>{d.title}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button style={{ background: C.gold+'22', color: C.gold, border: `1px solid ${C.gold}44`, borderRadius: 7, padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>+ New Doc</button>
      </div>

      {/* Doc content */}
      <div style={{ flex: 1, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 24, overflow: 'auto' }}>
        <div style={{ color: C.text, fontSize: 20, fontWeight: 700, marginBottom: 20 }}>{docs[selected]?.title}</div>
        <div style={{ color: C.text, fontSize: 13, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
          {docs[selected]?.content.split('\n').map((line, i) => {
            if (line.startsWith('# ')) return <h1 key={i} style={{ color: C.gold, fontSize: 18, fontWeight: 700, margin: '16px 0 10px' }}>{line.slice(2)}</h1>;
            if (line.startsWith('## ')) return <h2 key={i} style={{ color: C.blue, fontSize: 14, fontWeight: 700, margin: '14px 0 8px' }}>{line.slice(3)}</h2>;
            if (line.startsWith('- **')) return <div key={i} style={{ color: C.text, fontSize: 13, padding: '3px 0 3px 12px', borderLeft: `2px solid ${C.border}` }}>{line.slice(2)}</div>;
            if (line.startsWith('- ')) return <div key={i} style={{ color: C.muted, fontSize: 13, padding: '2px 0 2px 12px' }}>• {line.slice(2)}</div>;
            if (line.startsWith('- [ ] ')) return <div key={i} style={{ color: C.muted, fontSize: 12, padding: '3px 0 3px 12px', display: 'flex', gap: 8 }}><span style={{ color: C.dimmed }}>☐</span>{line.slice(6)}</div>;
            if (line.startsWith('**') && line.endsWith('**')) return <strong key={i} style={{ color: C.text }}>{line.slice(2,-2)}</strong>;
            if (line.startsWith('1.') || line.startsWith('2.') || line.startsWith('3.') || line.startsWith('4.') || line.startsWith('5.') || line.startsWith('6.') || line.startsWith('7.') || line.startsWith('8.') || line.startsWith('9.')) return <div key={i} style={{ color: C.text, fontSize: 13, padding: '3px 0 3px 12px' }}>{line}</div>;
            if (line === '') return <div key={i} style={{ height: 8 }} />;
            return <div key={i} style={{ color: C.muted, fontSize: 13, lineHeight: 1.7 }}>{line}</div>;
          })}
        </div>
      </div>
    </div>
  );
}

// ─── MEMORY VIEW ─────────────────────────────────────────────────────────────
function Memory() {
  const memData = [
    { section: '🎯 THE MISSION', content: '**$85K/month in merchant card processing residual income.** 15 months. Chris turns 50. We celebrate when we hit it.\n\nCurrent: $38,650/month (45.5% of goal). 456 days remaining.' },
    { section: '👥 The Crew', content: '• Agent 85 (me) — Chief of Staff, Mac Mini, running OpenClaw\n• Peter — Lead Hunter (Apollo)\n• Samir — Lead Enricher\n• Bolton — Email Writer\n• Milton — Email Monitor\n• Lumbergh — GHL Manager' },
    { section: '⚙️ The Setup', content: 'Mac Mini 16GB/256GB (now) → Mac Mini 32GB arriving ~March 26, 2026\nOpenClaw local. Mission Control localhost:3001. Discord primary channel.' },
    { section: '🚫 Rules', content: '1. NEVER run `openclaw doctor` — wipes config, causes amnesia\n2. tools.profile must stay = "full"\n3. Figure it out or build it. No excuses.\n4. Continuous autonomous velocity.' },
    { section: '📡 Pipeline', content: 'Apollo → enriches → Go High Level CRM\nNeeded: email sequences, landing pages, Stencil personalization, AI video funnels' },
  ];
  return (
    <div style={{ display: 'flex', gap: 14, height: '100%', overflow: 'hidden' }}>
      <div style={{ width: 220, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <input placeholder="Search memory..." style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 10px', color: C.text, fontSize: 12, outline: 'none', marginBottom: 6 }} />
        <div style={{ background: C.panelHover, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', cursor: 'pointer' }}>
          <div style={{ color: C.gold, fontSize: 12, fontWeight: 700 }}>Long-Term Memory 📌</div>
          <div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>5 sections • Updated today</div>
        </div>
        <div style={{ color: C.muted, fontSize: 10, letterSpacing: 2, marginTop: 8 }}>DAILY JOURNAL</div>
        {['Today (1)','Yesterday (0)','This Month (12)'].map(l=>(
          <div key={l} style={{ color: C.dimmed, fontSize: 11, padding: '4px 0', cursor: 'pointer' }}>▸ {l}</div>
        ))}
      </div>
      <div style={{ flex: 1, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20, overflow: 'auto' }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 18, marginBottom: 4 }}>MEMORY.md — Long-Term Memory</div>
        <div style={{ color: C.muted, fontSize: 11, marginBottom: 20 }}>The stuff worth keeping. Updated as we learn.</div>
        {memData.map(m => (
          <div key={m.section} style={{ marginBottom: 20 }}>
            <div style={{ color: C.gold, fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{m.section}</div>
            <div style={{ color: C.text, fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-line' }}>{m.content}</div>
            <div style={{ height: 1, background: C.border, marginTop: 16 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── TASKS VIEW ───────────────────────────────────────────────────────────────
function Tasks() {
  const [items, setItems] = useState([
    { id:1, title:'Fix Bolton email error', status:'in-progress', agent:'Bolton', project:'Email Sequences' },
    { id:2, title:'Review Apollo lead quality', status:'backlog', agent:'Peter', project:'Lead Pipeline' },
    { id:3, title:'Set up Stencil personalization', status:'backlog', agent:'Samir', project:'Landing Pages' },
    { id:4, title:'Connect real GHL API', status:'in-progress', agent:'Lumbergh', project:'GHL Integration' },
    { id:5, title:'Build AI video landing page template', status:'backlog', agent:'Agent 85', project:'Funnels' },
  ]);
  const [input, setInput] = useState('');
  const cols = ['backlog','in-progress','done'];
  const colLabels = { backlog:'Backlog', 'in-progress':'In Progress', done:'Done' };
  const colColors = { backlog: C.muted, 'in-progress': C.blue, done: C.green };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{ color: C.muted, fontSize: 12 }}>
          <b style={{ color: C.text }}>{items.filter(i=>i.status==='in-progress').length}</b> in progress &nbsp;
          <b style={{ color: C.text }}>{items.length}</b> total &nbsp;
          <b style={{ color: C.text }}>{Math.round(items.filter(i=>i.status==='done').length/Math.max(items.length,1)*100)}%</b> complete
        </div>
        <button style={{ marginLeft: 'auto', background: C.gold, color: '#000', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>+ New Task</button>
      </div>
      <div style={{ flex: 1, display: 'flex', gap: 12, overflow: 'auto' }}>
        {cols.map(col => (
          <div key={col} style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: colColors[col] }} />
              <span style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>{colLabels[col].toUpperCase()}</span>
              <span style={{ color: C.dimmed, fontSize: 11, marginLeft: 4 }}>{items.filter(i=>i.status===col).length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {items.filter(i=>i.status===col).map(item=>(
                <div key={item.id} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ color: C.text, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{item.title}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ background: C.blue+'22', color: C.blue, fontSize: 10, padding: '1px 6px', borderRadius: 3 }}>{item.agent}</span>
                    <span style={{ background: C.border, color: C.muted, fontSize: 10, padding: '1px 6px', borderRadius: 3 }}>{item.project}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── COMMAND VIEW ─────────────────────────────────────────────────────────────
function Command({ mission, agentData, projects }) {
  const fmt = n => '$' + (n||0).toLocaleString();
  const pct = mission ? Math.round((mission.current/mission.goal)*100) : 45;
  const chartData = [
    {m:'Jan',r:22000},{m:'Feb',r:28000},{m:'Mar',r:38650},{m:'Apr',r:null},
  ];
  return (
    <div style={{ display: 'flex', gap: 14, height: '100%', overflow: 'auto' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
          {[
            { label:'Monthly Residual', val: fmt(mission?.current||38650), color: C.green },
            { label:'Goal', val: fmt(mission?.goal||85000), color: C.gold },
            { label:'Progress', val:`${pct}%`, color: C.blue },
            { label:'Days to 50th', val:`${mission?.daysLeft||456} 🎂`, color: C.orange },
          ].map(s=>(
            <div key={s.label} style={{ background: C.panel, border:`1px solid ${C.border}`, borderRadius:10, padding:14 }}>
              <div style={{ color:C.muted, fontSize:10, letterSpacing:1, marginBottom:4 }}>{s.label.toUpperCase()}</div>
              <div style={{ color:s.color, fontWeight:700, fontSize:22 }}>{s.val}</div>
            </div>
          ))}
        </div>

        <div style={{ background: C.panel, border:`1px solid ${C.border}`, borderRadius:10, padding:14, height:170 }}>
          <div style={{ color:C.muted, fontSize:10, letterSpacing:2, marginBottom:8 }}>REVENUE TRAJECTORY → $85K</div>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={chartData}>
              <defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.gold} stopOpacity={0.3}/><stop offset="95%" stopColor={C.gold} stopOpacity={0}/></linearGradient></defs>
              <XAxis dataKey="m" tick={{fill:C.muted,fontSize:10}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fill:C.muted,fontSize:10}} axisLine={false} tickLine={false} tickFormatter={v=>'$'+v/1000+'k'}/>
              <Tooltip contentStyle={{background:C.panel,border:`1px solid ${C.border}`,color:C.text,fontSize:11}} formatter={v=>[fmt(v),'Revenue']}/>
              <Area type="monotone" dataKey="r" stroke={C.gold} fill="url(#rg)" strokeWidth={2} dot={{fill:C.gold,r:4}} connectNulls={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div style={{ background: C.panel, border:`1px solid ${C.border}`, borderRadius:10, padding:14, flex:1 }}>
          <div style={{ color:C.muted, fontSize:10, letterSpacing:2, marginBottom:10 }}>ACTIVE PROJECTS</div>
          {(projects||[]).map(p=>(
            <div key={p.id} style={{ marginBottom:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ color:C.text, fontSize:12 }}>{p.name}</span>
                <span style={{ color:C.muted, fontSize:11 }}>{p.progress}%</span>
              </div>
              <div style={{ height:4, background:C.border, borderRadius:2 }}>
                <div style={{ width:`${p.progress}%`, height:'100%', background:p.status==='active'?`linear-gradient(90deg,${C.blue},${C.gold})`:C.dimmed, borderRadius:2, transition:'width 1s ease' }}/>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ width:210, display:'flex', flexDirection:'column', gap:8 }}>
        {AGENTS.map(def=>{
          const a = agentData?.[def.id];
          return (
            <div key={def.id} style={{ background:C.panel, border:`1px solid ${def.id==='agent85'?C.gold+'44':C.border}`, borderRadius:8, padding:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:5 }}>
                <div style={{ width:26, height:26, borderRadius:'50%', background:def.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, boxShadow:`0 0 8px ${def.color}44` }}>{def.emoji}</div>
                <div>
                  <div style={{ color: def.id==='agent85'?C.gold:C.text, fontSize:12, fontWeight:700 }}>{def.name}</div>
                  <div style={{ color:C.muted, fontSize:10 }}>{def.role}</div>
                </div>
              </div>
              <StatusChip status={a?.status||'idle'}/>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── PING MODAL ───────────────────────────────────────────────────────────────
function PingModal({ onClose }) {
  const [msg, setMsg] = useState('');
  return (
    <div style={{ position:'fixed', inset:0, background:'#00000088', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <div style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:12, padding:24, width:440, backdropFilter:'blur(16px)' }} onClick={e=>e.stopPropagation()}>
        <div style={{ color:C.gold, fontWeight:700, fontSize:16, marginBottom:8 }}>🕵️ Ping Agent 85</div>
        <div style={{ color:C.muted, fontSize:12, marginBottom:14 }}>Send a message to Agent 85 via Discord</div>
        <textarea value={msg} onChange={e=>setMsg(e.target.value)} placeholder="What do you need?"
          style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:12, color:C.text, fontSize:13, outline:'none', resize:'vertical', minHeight:80, fontFamily:'inherit' }}/>
        <div style={{ display:'flex', gap:8, marginTop:12, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ background:'transparent', border:`1px solid ${C.border}`, color:C.muted, padding:'8px 16px', borderRadius:6, cursor:'pointer' }}>Cancel</button>
          <button onClick={onClose} style={{ background:C.gold, color:'#000', border:'none', padding:'8px 16px', borderRadius:6, cursor:'pointer', fontWeight:700 }}>Send</button>
        </div>
      </div>
    </div>
  );
}

// ─── CHART.JS LINE CHART COMPONENT ──────────────────────────────────────────
function ChartJsLine({ labels, data, label, color, gradientColor, onPointClick }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  useEffect(() => {
    const Chart = window.Chart;
    if (!Chart || !canvasRef.current) return;
    if (chartRef.current) chartRef.current.destroy();
    const ctx = canvasRef.current.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
    gradient.addColorStop(0, (gradientColor || color) + '44');
    gradient.addColorStop(1, (gradientColor || color) + '00');
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: label || 'Value',
          data,
          borderColor: color || C.green,
          backgroundColor: gradient,
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          pointBackgroundColor: color || C.green,
          pointBorderColor: color || C.green,
          borderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: C.panel,
            titleColor: C.text,
            bodyColor: C.green,
            borderColor: C.border,
            borderWidth: 1,
            callbacks: { label: (ctx) => '$' + ctx.parsed.y.toLocaleString() },
          },
        },
        scales: {
          x: { ticks: { color: C.muted, font: { size: 10 } }, grid: { color: C.border + '44' } },
          y: { ticks: { color: C.muted, font: { size: 10 }, callback: v => '$' + Math.round(v / 1000) + 'k' }, grid: { color: C.border + '44' } },
        },
      },
    });
    // Add click handler for point selection
    if (onPointClick && canvasRef.current) {
      canvasRef.current.onclick = (e) => {
        const pts = chartRef.current.getElementsAtEventForMode(e, 'nearest', { intersect: true }, true);
        if (pts.length > 0) onPointClick(pts[0].index);
      };
    }
    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [labels, data, label, color, gradientColor, onPointClick]);
  return <canvas ref={canvasRef} style={{ cursor: onPointClick ? 'pointer' : 'default' }} />;
}

// ─── RESIDUALS PANEL (API-driven + Chart.js) ────────────────────────────────
function ResidualsPanel() {
  const [resData, setResData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedResidualMonth, setSelectedResidualMonth] = useState(null);

  useEffect(() => {
    fetch('/api/residuals').then(r => r.json()).then(d => { setResData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: C.muted, padding: 40, textAlign: 'center' }}>Loading residuals data...</div>;
  if (!resData || !resData.months.length) return <div style={{ color: C.muted, padding: 40 }}>No residuals data available.</div>;

  const { months, ytd2026, bestMonth, currentMonth, momChange } = resData;
  const GOAL = 85000;
  const pct = Math.min(100, Math.round((currentMonth.total / GOAL) * 100));
  const reversed = [...months].reverse();
  const chartLabels = reversed.map(d => d.month.split(' ')[0].slice(0, 3) + ' ' + d.month.split(' ')[1].slice(2));
  const chartValues = reversed.map(d => Math.round(d.total));
  const trendUp = momChange >= 0;
  const fmt = n => '$' + Math.round(n).toLocaleString();

  const cellStyle = { padding: '8px 12px', borderBottom: `1px solid ${C.border}`, textAlign: 'right' };
  const headerCell = { ...cellStyle, color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 };

  return (
    <div style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{ margin: 0, color: C.gold, fontSize: 18 }}>Residuals Dashboard</h2>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {[
          { label: 'Current Month', val: fmt(currentMonth.total), sub: currentMonth.month, col: C.green },
          { label: 'YTD 2026', val: fmt(ytd2026), sub: 'Year to date', col: C.blue },
          { label: 'Best Month', val: fmt(bestMonth.total), sub: bestMonth.month, col: C.gold },
          { label: 'MoM Trend', val: `${trendUp ? '▲' : '▼'} ${Math.abs(momChange)}%`, sub: trendUp ? 'Up from last month' : 'Down from last month', col: trendUp ? C.green : C.red },
        ].map(s => (
          <div key={s.label} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
            <div style={{ color: C.muted, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>{s.label.toUpperCase()}</div>
            <div style={{ color: s.col, fontWeight: 700, fontSize: 22 }}>{s.val}</div>
            <div style={{ color: C.dimmed, fontSize: 10, marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Goal Tracker */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ color: C.muted, fontSize: 12 }}>Monthly Goal Progress</span>
          <span style={{ color: C.gold, fontSize: 13, fontWeight: 600 }}>{fmt(currentMonth.total)} / {fmt(GOAL)}</span>
        </div>
        <div style={{ background: C.bg, borderRadius: 6, height: 18, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${C.green}, ${C.gold})`, borderRadius: 6, transition: 'width 0.5s' }} />
        </div>
        <div style={{ textAlign: 'right', marginTop: 4, fontSize: 11, color: C.muted }}>{pct}% of $85k goal</div>
      </div>

      {/* Chart.js Line Chart — Clickable */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ color: C.muted, fontSize: 12 }}>Rep Net Income — 13 Month Trend <span style={{ fontSize: 10 }}>(click a dot)</span></div>
          {selectedResidualMonth && <span onClick={() => setSelectedResidualMonth(null)} style={{ color: C.blue, fontSize: 11, cursor: 'pointer' }}>← Show All</span>}
        </div>
        {selectedResidualMonth && (
          <div style={{ background: C.bg, borderRadius: 8, padding: 12, marginBottom: 12, border: `1px solid ${C.gold}` }}>
            <div style={{ color: C.gold, fontWeight: 700, marginBottom: 4 }}>{selectedResidualMonth.month}</div>
            <div style={{ color: C.green, fontSize: 22, fontWeight: 700 }}>{fmt(selectedResidualMonth.total)}</div>
            <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>Rep Net for this month</div>
          </div>
        )}
        <div style={{ height: 220 }}>
          <ChartJsLine labels={chartLabels} data={chartValues} label="Rep Net" color={C.green} onPointClick={idx => {
            const m = reversed[idx];
            if (m) setSelectedResidualMonth(m === selectedResidualMonth ? null : m);
          }} />
        </div>
      </div>

      {/* Summary Table — clickable rows */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ ...headerCell, textAlign: 'left' }}>Month</th>
              <th style={headerCell}>Rep Net</th>
              <th style={headerCell}>vs Prev</th>
            </tr>
          </thead>
          <tbody>
            {months.map((d, i) => {
              const prev = months[i + 1];
              const diff = prev ? d.total - prev.total : 0;
              const diffPct = prev && prev.total > 0 ? ((d.total - prev.total) / prev.total * 100) : 0;
              const isSelected = selectedResidualMonth?.month === d.month;
              return (
                <tr key={d.month} onClick={() => setSelectedResidualMonth(isSelected ? null : d)}
                  style={{ cursor: 'pointer', background: isSelected ? C.gold + '22' : 'transparent' }}>
                  <td style={{ ...cellStyle, textAlign: 'left', color: isSelected ? C.gold : C.text }}>{d.month}</td>
                  <td style={{ ...cellStyle, color: C.green, fontWeight: 600 }}>{fmt(d.total)}</td>
                  <td style={{ ...cellStyle, color: diff >= 0 ? C.green : C.red, fontSize: 12 }}>
                    {prev ? `${diff >= 0 ? '▲' : '▼'} ${Math.abs(Math.round(diffPct * 10) / 10)}%` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MERCHANTS TABLE ─────────────────────────────────────────────────────────
function MerchantsPanel({ onSelectMerchant }) {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('repNet');
  const [sortDir, setSortDir] = useState(-1);

  useEffect(() => {
    fetch('/api/merchants').then(r => r.json()).then(d => { setMerchants(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d * -1);
    else { setSortBy(col); setSortDir(-1); }
  };

  const filtered = merchants.filter(m => {
    const q = search.toLowerCase();
    return !q || m.name.toLowerCase().includes(q) || m.mid.toLowerCase().includes(q) || m.book.toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const va = typeof a[sortBy] === 'string' ? a[sortBy].toLowerCase() : a[sortBy];
    const vb = typeof b[sortBy] === 'string' ? b[sortBy].toLowerCase() : b[sortBy];
    return va < vb ? -sortDir : va > vb ? sortDir : 0;
  });

  const fmt = n => '$' + Math.round(n).toLocaleString();
  const cellStyle = { padding: '8px 12px', borderBottom: `1px solid ${C.border}`, fontSize: 12 };
  const headerCell = { ...cellStyle, color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'pointer', userSelect: 'none' };
  const sortIcon = (col) => sortBy === col ? (sortDir === -1 ? ' ▼' : ' ▲') : '';

  if (loading) return <div style={{ color: C.muted, padding: 40, textAlign: 'center' }}>Loading merchants...</div>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ margin: 0, color: C.gold, fontSize: 18 }}>Merchants</h2>
        <span style={{ color: C.muted, fontSize: 12 }}>{filtered.length} merchants</span>
        <div style={{ marginLeft: 'auto' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, MID, or book..."
            style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px', color: C.text, fontSize: 12, outline: 'none', width: 260 }} />
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, background: C.panel, zIndex: 2 }}>
            <tr>
              <th style={{ ...headerCell, textAlign: 'left' }} onClick={() => handleSort('name')}>Merchant Name{sortIcon('name')}</th>
              <th style={headerCell} onClick={() => handleSort('mid')}>MID{sortIcon('mid')}</th>
              <th style={{ ...headerCell, textAlign: 'right' }} onClick={() => handleSort('volume')}>Volume{sortIcon('volume')}</th>
              <th style={{ ...headerCell, textAlign: 'right' }} onClick={() => handleSort('repNet')}>Rep Net{sortIcon('repNet')}</th>
              <th style={{ ...headerCell, textAlign: 'left' }} onClick={() => handleSort('book')}>Book{sortIcon('book')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(m => (
              <tr key={m.mid} onClick={() => onSelectMerchant(m.mid)}
                style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = C.panelHover}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ ...cellStyle, textAlign: 'left', color: C.text, fontWeight: 600 }}>{m.name}</td>
                <td style={{ ...cellStyle, color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>{m.mid}</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: C.text }}>{fmt(m.volume)}</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: C.green, fontWeight: 600 }}>{fmt(m.repNet)}</td>
                <td style={{ ...cellStyle, textAlign: 'left', color: C.muted, fontSize: 11, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.book}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MERCHANT PROFILE ────────────────────────────────────────────────────────
function MerchantProfile({ mid, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSelectedMonth(null);
    fetch(`/api/merchants/${encodeURIComponent(mid)}`).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [mid]);

  if (loading) return <div style={{ color: C.muted, padding: 40, textAlign: 'center' }}>Loading merchant...</div>;
  if (!data || data.error) return <div style={{ color: C.red, padding: 40 }}>Merchant not found.</div>;

  const fmt = n => '$' + Math.round(n).toLocaleString();
  const months = Object.entries(data.months || {});
  // Sort months chronologically
  const monthOrder = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  months.sort((a, b) => {
    const [mA, yA] = a[0].split(' ');
    const [mB, yB] = b[0].split(' ');
    return (parseInt(yA) - parseInt(yB)) || (monthOrder.indexOf(mA) - monthOrder.indexOf(mB));
  });

  const chartLabels = months.map(([m]) => m.split(' ')[0].slice(0, 3) + ' ' + m.split(' ')[1].slice(2));
  const chartValues = months.map(([, d]) => Math.round(d.repNet * 100) / 100);

  // Active display: selected month or totals
  const activeData = selectedMonth ? data.months[selectedMonth] : data.totals;
  const activeLabel = selectedMonth || 'ALL TIME';

  const contact = data.contact;
  const cellStyle = { padding: '8px 12px', borderBottom: `1px solid ${C.border}`, fontSize: 12 };
  const headerCell = { ...cellStyle, color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 };

  return (
    <div style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Back link */}
      <div>
        <button onClick={onBack} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
          ← Back to Merchants
        </button>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: C.blue + '22', border: `2px solid ${C.blue}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🏪</div>
        <div>
          <h2 style={{ margin: 0, color: C.text, fontSize: 20 }}>{data.name}</h2>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>MID: <span style={{ fontFamily: 'monospace' }}>{data.mid}</span> &nbsp;|&nbsp; Book: {data.book} &nbsp;|&nbsp; Rep %: {data.repPercent}</div>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: -8 }}>
        <div style={{ color: C.muted, fontSize: 11 }}>📊 {activeLabel} {selectedMonth && <span onClick={() => setSelectedMonth(null)} style={{ color: C.blue, cursor: 'pointer', marginLeft: 8 }}>← Show All Time</span>}</div>
        {months.length > 1 && <div style={{ display: 'flex', gap: 6 }}>
          {months.map(([m]) => <button key={m} onClick={() => setSelectedMonth(m === selectedMonth ? null : m)} style={{ background: m === selectedMonth ? C.blue : 'transparent', border: `1px solid ${m === selectedMonth ? C.blue : C.border}`, color: m === selectedMonth ? '#fff' : C.muted, padding: '2px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 10 }}>{m.split(' ')[0].slice(0,3)}</button>)}
        </div>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {[
          { label: 'Volume', val: fmt(activeData?.volume||0), col: C.text },
          { label: 'Transactions', val: (activeData?.transactions||0).toLocaleString(), col: C.blue },
          { label: 'Gross Fees', val: fmt(activeData?.revenue||0), col: C.orange, sub: 'Total fees charged' },
          { label: 'Interchange', val: fmt(activeData?.costs||0), col: '#e05a5a', sub: 'Visa/MC/Amex cut' },
          { label: 'Payroc Net Income', val: fmt((activeData?.revenue||0)-(activeData?.costs||0)), col: '#f0a500', sub: 'After interchange' },
          { label: 'Rep Net', val: fmt(activeData?.repNet||0), col: C.green, sub: `Your ${data.repPercent||''}% split` },
        ].map(s => (
          <div key={s.label} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
            <div style={{ color: C.muted, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>{s.label.toUpperCase()}</div>
            <div style={{ color: s.col, fontWeight: 700, fontSize: 20 }}>{s.val}</div>
            {s.sub && <div style={{ color: C.muted, fontSize: 9, marginTop: 2, opacity: 0.7 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Contact Info */}
      {contact && Object.keys(contact).length > 0 && (
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ color: C.muted, fontSize: 10, letterSpacing: 2, marginBottom: 10 }}>CONTACT INFO</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, fontSize: 13 }}>
            {(contact.owner || contact.ownerName || contact.contactName) && <div><span style={{ color: C.muted }}>Contact:</span> <span style={{ color: C.text }}>{contact.owner || contact.ownerName || contact.contactName}{contact.contactTitle ? ` (${contact.contactTitle})` : ''}</span></div>}
            {contact.name && <div><span style={{ color: C.muted }}>Name:</span> <span style={{ color: C.text }}>{contact.name}</span></div>}
            {contact.phone && <div><span style={{ color: C.muted }}>Phone:</span> <span style={{ color: C.text }}>{contact.phone}</span></div>}
            {contact.email && <div><span style={{ color: C.muted }}>Email:</span> <span style={{ color: C.blue }}>{contact.email}</span></div>}
            {contact.address && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: C.muted }}>Address:</span> <span style={{ color: C.text }}>{contact.address}{contact.city ? `, ${contact.city}` : ''}{contact.state ? `, ${contact.state}` : ''}{contact.zip ? ` ${contact.zip}` : ''}</span></div>}
          </div>
        </div>
      )}

      {/* Revenue History Chart */}
      {months.length > 0 && (
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ color: C.muted, fontSize: 12, marginBottom: 8 }}>Monthly Rep Net History <span style={{ color: C.muted, fontSize: 10 }}>(click a dot to filter KPIs)</span></div>
          <div style={{ height: 200 }}>
            <ChartJsLine labels={chartLabels} data={chartValues} label="Rep Net" color={C.green} onPointClick={idx => { const m = months[idx]; if(m) setSelectedMonth(m[0] === selectedMonth ? null : m[0]); }} />
          </div>
        </div>
      )}

      {/* Monthly Breakdown Table */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...headerCell, textAlign: 'left' }}>Month</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Volume</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Transactions</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Revenue</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Rep Net</th>
            </tr>
          </thead>
          <tbody>
            {[...months].reverse().map(([month, d]) => (
              <tr key={month}>
                <td style={{ ...cellStyle, textAlign: 'left', color: C.text }}>{month}</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: C.text }}>{fmt(d.volume)}</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: C.muted }}>{d.transactions}</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: C.orange }}>{fmt(d.revenue)}</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: C.green, fontWeight: 600 }}>{fmt(d.repNet)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── REPORTS PANEL ──────────────────────────────────────────────────────────
function ReportsPanel() {
  const [topByRepPay, setTopByRepPay] = useState([]);
  const [topByVolume, setTopByVolume] = useState([]);
  const [momChanges, setMomChanges] = useState({ months: [], changes: [] });
  const [segments, setSegments] = useState({ books: [], volumeRanges: [], totalMerchants: 0 });
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState('rep-pay');
  const [limit, setLimit] = useState(20);
  const [minVolume, setMinVolume] = useState('');
  const [minRepPay, setMinRepPay] = useState('');
  const [bookFilter, setBookFilter] = useState('');

  const buildQuery = (extra = '') => {
    const params = new URLSearchParams();
    params.set('limit', limit);
    if (minVolume) params.set('minVolume', minVolume);
    if (minRepPay) params.set('minRepPay', minRepPay);
    if (bookFilter) params.set('book', bookFilter);
    return params.toString() + (extra ? '&' + extra : '');
  };

  const fetchData = () => {
    setLoading(true);
    const q = buildQuery();
    Promise.all([
      fetch(`/api/reports/top-by-rep-pay?${q}`).then(r => r.json()),
      fetch(`/api/reports/top-by-volume?${q}`).then(r => r.json()),
      fetch('/api/reports/mom-changes').then(r => r.json()),
      fetch('/api/reports/segments').then(r => r.json()),
    ]).then(([repPay, vol, mom, seg]) => {
      setTopByRepPay(repPay);
      setTopByVolume(vol);
      setMomChanges(mom);
      setSegments(seg);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [limit, minVolume, minRepPay, bookFilter]);

  const fmt = n => '$' + Math.round(n).toLocaleString();
  const cellStyle = { padding: '8px 12px', borderBottom: `1px solid ${C.border}`, fontSize: 12 };
  const headerCell = { ...cellStyle, color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 };

  const exportCsv = () => {
    const data = activeReport === 'rep-pay' ? topByRepPay : activeReport === 'volume' ? topByVolume : momChanges.changes;
    if (!data || !data.length) return;
    const headers = activeReport === 'mom'
      ? ['Merchant Name','MID','Current Volume','Current Rep Net','Volume Change','Rep Net Change','Volume Change %','Rep Net Change %']
      : ['Merchant Name','MID','Volume','Rep Net','Book','Transactions'];
    const rows = data.map(m => activeReport === 'mom'
      ? [m.name, m.mid, m.currentVolume, m.currentRepNet, m.volumeChange, m.repNetChange, m.volumeChangePct + '%', m.repNetChangePct + '%']
      : [m.name, m.mid, m.volume, m.repNet, m.book, m.transactions]
    );
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${activeReport}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'rep-pay', label: 'Top by Rep Pay' },
    { id: 'volume', label: 'Top by Volume' },
    { id: 'mom', label: 'MoM Changes' },
  ];

  if (loading) return <div style={{ color: C.muted, padding: 40, textAlign: 'center' }}>Loading reports...</div>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ margin: 0, color: C.gold, fontSize: 18 }}>Reports</h2>
        <span style={{ color: C.muted, fontSize: 12 }}>{segments.totalMerchants} merchants total</span>
      </div>

      {/* KPI Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        {[
          { label: 'Top Rep Pay', val: topByRepPay[0] ? fmt(topByRepPay[0].repNet) : '—', sub: topByRepPay[0]?.name || '', col: C.green },
          { label: 'Top Volume', val: topByVolume[0] ? fmt(topByVolume[0].volume) : '—', sub: topByVolume[0]?.name || '', col: C.blue },
          { label: 'Avg Rep Pay', val: topByRepPay.length ? fmt(topByRepPay.reduce((s, m) => s + m.repNet, 0) / topByRepPay.length) : '—', sub: `Across ${topByRepPay.length} merchants`, col: C.orange },
          { label: 'Total Volume', val: fmt(topByVolume.reduce((s, m) => s + m.volume, 0)), sub: 'Filtered total', col: C.gold },
        ].map(s => (
          <div key={s.label} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
            <div style={{ color: C.muted, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>{s.label.toUpperCase()}</div>
            <div style={{ color: s.col, fontWeight: 700, fontSize: 20 }}>{s.val}</div>
            <div style={{ color: C.dimmed, fontSize: 10, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: 1 }}>FILTERS</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ color: C.dimmed, fontSize: 11 }}>Book:</label>
          <select value={bookFilter} onChange={e => setBookFilter(e.target.value)}
            style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 8px', color: C.text, fontSize: 11 }}>
            <option value="">All</option>
            {segments.books.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ color: C.dimmed, fontSize: 11 }}>Min Volume:</label>
          <input value={minVolume} onChange={e => setMinVolume(e.target.value)} placeholder="e.g. 5000"
            style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 8px', color: C.text, fontSize: 11, width: 90 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ color: C.dimmed, fontSize: 11 }}>Min Rep Pay:</label>
          <input value={minRepPay} onChange={e => setMinRepPay(e.target.value)} placeholder="e.g. 50"
            style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 8px', color: C.text, fontSize: 11, width: 90 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ color: C.dimmed, fontSize: 11 }}>Limit:</label>
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value))}
            style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 8px', color: C.text, fontSize: 11 }}>
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <button onClick={exportCsv} style={{ marginLeft: 'auto', background: C.green + '22', border: `1px solid ${C.green}44`, color: C.green, padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
          Export CSV
        </button>
      </div>

      {/* Report Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${C.border}` }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveReport(t.id)} style={{
            background: activeReport === t.id ? C.panel : 'transparent',
            border: 'none', borderBottom: activeReport === t.id ? `2px solid ${C.gold}` : '2px solid transparent',
            color: activeReport === t.id ? C.text : C.muted,
            padding: '8px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Table Content */}
      {activeReport !== 'mom' && (
        <div style={{ flex: 1, overflow: 'auto', background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: C.panel, zIndex: 2 }}>
              <tr>
                <th style={{ ...headerCell, textAlign: 'center', width: 40 }}>#</th>
                <th style={{ ...headerCell, textAlign: 'left' }}>Merchant Name</th>
                <th style={headerCell}>MID</th>
                <th style={{ ...headerCell, textAlign: 'right' }}>Volume</th>
                <th style={{ ...headerCell, textAlign: 'right' }}>Rep Net</th>
                <th style={{ ...headerCell, textAlign: 'left' }}>Book</th>
              </tr>
            </thead>
            <tbody>
              {(activeReport === 'rep-pay' ? topByRepPay : topByVolume).map((m, i) => (
                <tr key={m.mid} style={{ transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = C.panelHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ ...cellStyle, textAlign: 'center', color: i < 3 ? C.gold : C.dimmed, fontWeight: i < 3 ? 700 : 400 }}>{i + 1}</td>
                  <td style={{ ...cellStyle, textAlign: 'left', color: C.text, fontWeight: 600 }}>{m.name}</td>
                  <td style={{ ...cellStyle, color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>{m.mid}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', color: activeReport === 'volume' ? C.blue : C.text }}>{fmt(m.volume)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', color: activeReport === 'rep-pay' ? C.green : C.text, fontWeight: activeReport === 'rep-pay' ? 700 : 400 }}>{fmt(m.repNet)}</td>
                  <td style={{ ...cellStyle, textAlign: 'left', color: C.muted, fontSize: 11, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.book}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(activeReport === 'rep-pay' ? topByRepPay : topByVolume).length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: C.muted }}>No merchants match the current filters.</div>
          )}
        </div>
      )}

      {activeReport === 'mom' && (
        <div style={{ flex: 1, overflow: 'auto', background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          {momChanges.months.length < 2 ? (
            <div style={{ padding: 30, textAlign: 'center', color: C.muted }}>
              Month-over-month comparison requires at least 2 months of data. Currently have: {momChanges.months.join(', ') || 'none'}.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: C.panel, zIndex: 2 }}>
                <tr>
                  <th style={{ ...headerCell, textAlign: 'left' }}>Merchant</th>
                  <th style={headerCell}>MID</th>
                  <th style={{ ...headerCell, textAlign: 'right' }}>Current Vol</th>
                  <th style={{ ...headerCell, textAlign: 'right' }}>Vol Change</th>
                  <th style={{ ...headerCell, textAlign: 'right' }}>Current Rep Net</th>
                  <th style={{ ...headerCell, textAlign: 'right' }}>Rep Net Change</th>
                </tr>
              </thead>
              <tbody>
                {momChanges.changes.map(m => (
                  <tr key={m.mid} style={{ transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = C.panelHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...cellStyle, textAlign: 'left', color: C.text, fontWeight: 600 }}>{m.name}</td>
                    <td style={{ ...cellStyle, color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>{m.mid}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', color: C.text }}>{fmt(m.currentVolume)}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', color: m.volumeChange >= 0 ? C.green : C.red, fontWeight: 600 }}>
                      {m.volumeChange >= 0 ? '▲' : '▼'} {fmt(Math.abs(m.volumeChange))} ({m.volumeChangePct}%)
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right', color: C.green }}>{fmt(m.currentRepNet)}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', color: m.repNetChange >= 0 ? C.green : C.red, fontWeight: 600 }}>
                      {m.repNetChange >= 0 ? '▲' : '▼'} {fmt(Math.abs(m.repNetChange))} ({m.repNetChangePct}%)
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [section, setSection] = useState('office');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const [state, setState] = useState({ mission:{ goal:85000, current:38650, daysLeft:456 }, agents:{}, projects:[
    {id:1,name:'Apollo Lead Pipeline',status:'active',progress:68,agent:'peter'},
    {id:2,name:'Email Sequences v2',status:'active',progress:45,agent:'bolton'},
    {id:3,name:'GHL Booking Automation',status:'active',progress:30,agent:'lumbergh'},
    {id:4,name:'AI Landing Page System',status:'planned',progress:0,agent:'samir'},
    {id:5,name:'Stencil Personalization',status:'planned',progress:0,agent:'samir'},
  ]});
  const [waterCooler, setWaterCooler] = useState([]);
  const [ping, setPing] = useState(false);
  const [selectedMid, setSelectedMid] = useState(null);

  useEffect(() => {
    socket.on('state', setState);
    socket.on('waterCooler', setWaterCooler);
    fetch('/api/state').then(r=>r.json()).then(d=>{ setState(prev=>({...prev,...d})); setWaterCooler(d.waterCooler||[]); }).catch(()=>{});
    return () => { socket.off('state',setState); socket.off('waterCooler',setWaterCooler); };
  }, []);

  // Handle URL-based merchant routing
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/merchant\/(.+)$/);
    if (match) {
      setSection('merchant-profile');
      setSelectedMid(decodeURIComponent(match[1]));
    }
  }, []);

  const handleSelectMerchant = (mid) => {
    setSelectedMid(mid);
    setSection('merchant-profile');
    window.history.pushState({}, '', `/merchant/${encodeURIComponent(mid)}`);
  };

  const handleBackToMerchants = () => {
    setSelectedMid(null);
    setSection('merchants');
    window.history.pushState({}, '', '/');
  };

  const views = {
    tasks:     <Tasks />,
    pipeline:  <Pipeline />,
    approvals: <Approvals />,
    council:   <div style={{color:C.muted,padding:20}}>Council — coming soon</div>,
    calendar:  <Calendar />,
    projects:  <div style={{color:C.muted,padding:20}}>Projects detailed view — coming soon</div>,
    memory:    <Memory />,
    docs:      <Docs />,
    people:    <div style={{color:C.muted,padding:20}}>People — coming soon</div>,
    office:    <Office agentData={state.agents} waterCooler={waterCooler} />,
    residuals: <ResidualsPanel />,
    merchants: <MerchantsPanel onSelectMerchant={handleSelectMerchant} />,
    'merchant-profile': selectedMid ? <MerchantProfile mid={selectedMid} onBack={handleBackToMerchants} /> : null,
    reports:   <ReportsPanel />,
    team:      <Team />,
  };

  return (
    <div style={{ height:'100vh', display:'flex', flexDirection:'column', background:C.bg, fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", position:'relative', overflow:'hidden' }}>
      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}
        * { box-sizing: border-box; }
        input,textarea,button { font-family: inherit; }
      `}</style>

      <Starfield />

      <div style={{ position:'relative', zIndex:1, height:'100%', display:'flex', flexDirection:'column' }}>
        <TickerBanner />
        <MissionBar mission={state.mission} onPing={()=>setPing(true)} />
        <div style={{ flex:1, display:'flex', overflow:'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
          {/* Sidebar — desktop only */}
          {!isMobile && (
          <div style={{ width:172, background:C.sidebar+'dd', borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', padding:'10px 0', backdropFilter:'blur(12px)' }}>
            {NAV.map(n => (
              <button key={n.id} onClick={()=>setSection(n.id)} style={{
                display:'flex', alignItems:'center', gap:9, padding:'8px 14px',
                background: section===n.id ? C.border+'aa' : 'transparent',
                border:'none', borderLeft: section===n.id ? `2px solid ${C.gold}` : '2px solid transparent',
                color: section===n.id ? C.text : C.muted,
                cursor:'pointer', textAlign:'left', fontSize:13, fontFamily:'inherit',
                transition:'all 0.12s',
              }}>
                <span style={{fontSize:14}}>{n.icon}</span> {n.label}
              {n.badge && <span style={{ marginLeft:'auto', background: C.red, color:'#fff', borderRadius:'50%', width:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700 }}>{n.badge}</span>}
              </button>
            ))}
          </div>
          )}
          {/* Content */}
          <div style={{ flex:1, padding: isMobile ? 10 : 16, overflow:'hidden', display:'flex', flexDirection:'column', backdropFilter:'blur(2px)', paddingBottom: isMobile ? 80 : 16 }}>
            <div style={{ flex:1, overflow: 'auto' }}>{views[section]}</div>
          </div>
          {/* Mobile bottom nav — fixed to bottom of screen */}
          {isMobile && (
          <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:1000, height: 60, paddingBottom: 'env(safe-area-inset-bottom)', background: C.sidebar+'f5', borderTop: `1px solid ${C.border}`, display:'flex', overflowX:'auto', WebkitOverflowScrolling:'touch', backdropFilter:'blur(12px)' }}>
            {NAV.map(n => (
              <button key={n.id} onClick={()=>setSection(n.id)} style={{
                flex: '0 0 auto', minWidth: 64, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap: 2,
                background: section===n.id ? C.border+'aa' : 'transparent',
                border:'none', borderTop: section===n.id ? `2px solid ${C.gold}` : '2px solid transparent',
                color: section===n.id ? C.text : C.muted,
                cursor:'pointer', fontSize:9, fontFamily:'inherit', padding: '4px 8px',
              }}>
                <span style={{fontSize:18}}>{n.icon}</span>
                <span style={{fontSize:9, whiteSpace:'nowrap'}}>{n.label}</span>
                {n.badge && <span style={{ background: C.red, color:'#fff', borderRadius:'50%', width:12, height:12, display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:700, position:'absolute', marginLeft:20, marginTop:-18 }}>{n.badge}</span>}
              </button>
            ))}
          </div>
          )}
        </div>
      </div>

      {ping && <PingModal onClose={()=>setPing(false)} />}
    </div>
  );
}
